// Frontend-local shared control: localization changes presentation only.
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Box, HBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { IconClose } from "@ui/icons/IconClose";
import Spacer from "@ui/spacer/Spacer";
import { Spinner } from "@ui/spinner/Spinner";
import { Typo, TypoSmall } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { useWindowSize } from "@ui/utils/useResize";

// Спасибо Вова что ты запорол и этот компонент
interface IPanelProps {
	children?: any;
	scrollable?: boolean;
	decorator?: "absolute";
	busy?: boolean;
	label?: ReactNode;
	headerActions?: ReactNode;
	sx?: RecursiveCSS;
	onClose?: () => void;
	contentPadding?: string;
	disableScroll?: boolean;
	growContent?: boolean;
}

const Panel = ({ children, onClose, label, headerActions, scrollable, decorator, busy, sx, contentPadding, disableScroll, growContent }: IPanelProps) => {
	useLocale();
	//const theme = useTheme() as ITheme;

	const { width } = useWindowSize();
	const isMobile = width <= 768;
	const isMobileSheet = isMobile && decorator === "absolute";

	const [sheetVisible, setSheetVisible] = useState(false);

	useEffect(() => {
		if (isMobileSheet) {
			// trigger enter animation on next frame
			requestAnimationFrame(() => setSheetVisible(true));
		} else {
			setSheetVisible(false);
		}
	}, [isMobileSheet]);

	useEffect(() => {
		if (decorator === "absolute") {
			// A panel opened over another one must not unlock page scrolling when it closes.
			const previous = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			return () => {
				document.body.style.overflow = previous;
			};
		}
	}, [decorator]);

	const handleClose = useCallback(() => {
		if (isMobileSheet) {
			setSheetVisible(false);
			setTimeout(() => onClose?.(), 300);
		} else {
			onClose?.();
		}
	}, [isMobileSheet, onClose]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				handleClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [handleClose]);

	const handleBackdropClick = useCallback((e?: MouseEvent) => {
		// Не правильное решение, так как может быть несколько панелей, плюс я могу начать выделять на панели что то и заехать за границу.
		if (e?.target === e?.currentTarget) {
			//onClose?.();
		}
	}, []);

	if (decorator === "absolute" && scrollable) {
		children = (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					maxHeight: growContent ? undefined : "80vh",
					zIndex: 1000,
					height: growContent ? undefined : "100%",
					minHeight: 0,
					flex: growContent ? "1 1 auto" : undefined,
					overflow: "auto",
				}}>
				{children}
			</Box>
		);
	}

	const childrenContent = label ? (
		<Box
			sx={{
				padding: contentPadding ?? "20px",
				boxSizing: "border-box",
				minHeight: 0,
				overflowX: "hidden",
				overflowY: scrollable && growContent ? "hidden" : disableScroll ? "hidden" : "auto",
				flex: growContent ? "1 1 auto" : "1 1 0",
				display: "flex",
				flexDirection: "column",
			}}>
			{children}
		</Box>
	) : (
		children
	);

	const mobileSheetSx: RecursiveCSS = isMobileSheet
		? {
				width: "100%",
				minWidth: "unset",
				maxWidth: "100%",
				height: "92vh",
				maxHeight: "92vh",
				minHeight: "unset",
				borderRadius: "16px 16px 0 0",
			}
		: {};

	const content = (
		<Box
			sx={{
				border: "1px solid rgba(255, 255, 255, 0.05)",
				padding: !label ? (isMobileSheet ? "16px" : "30px") : undefined,
				borderRadius: "6px",
				boxShadow: "0px 0px 100px rgba(0,0,0,.1)",
				pointerEvents: busy ? "none" : "auto",
				userSelect: busy ? "none" : "auto",
				display: "flex",
				position: "relative",
				flexDirection: "column",
				backgroundColor: "panelBackgroundPrimary",
				maxHeight: "95vh",

				...sx,
				...mobileSheetSx,
			}}>
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(255,255,255,.5)",
						zIndex: 100000,
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						justifyContent: "center",
						alignItems: "center",
					}}>
					<Spinner color="#ad1bf1" />
					<TypoSmall>{msg("sharedPleasewait")}</TypoSmall>
				</Box>
			)}
			{isMobileSheet && (
				<Box sx={{ display: "flex", justifyContent: "center", paddingTop: "6px", paddingBottom: "2px" }}>
					<Box sx={{ width: "36px", height: "4px", borderRadius: "2px", backgroundColor: "rgba(0,0,0,0.2)" }} />
				</Box>
			)}
			{label && (
				<>
					<HBox
						sx={{
							backgroundColor: isMobileSheet ? undefined : decorator === "absolute" ? "panelHeadBackgroundColor" : undefined,
							padding: isMobileSheet ? "6px 16px" : "20px",
							boxSizing: "border-box",
							width: "100%",
							alignItems: "center",
							flexShrink: 0,
						}}>
						<Typo
							variant="head"
							sx={{
								display: "flex",
								paddingRight: "30px",
								overflowWrap: "break-word",
								wordBreak: "break-all",
								width: "100%",
								minWidth: headerActions ? 0 : undefined,
								fontWeight: isMobileSheet ? "600" : decorator === "absolute" ? "200" : "500",
								fontSize: isMobileSheet ? "1rem" : decorator === "absolute" ? "1.4rem" : "1.2rem",
							}}>
							{label}
						</Typo>
						{headerActions && <HBox sx={{ flexShrink: 0, alignItems: "center", marginRight: "6px" }}>{headerActions}</HBox>}
						{onClose && (
							<>
								<Spacer grow />
								<Button variant="icon" icon={<IconClose />} sx={{ padding: "10px !important" }} onClick={handleClose} />
							</>
						)}
					</HBox>
					<Box
						sx={{
							boxSizing: "border-box",
							background: "panelDividerBackgroundColor",
							height: isMobileSheet ? "1px" : "2px",
							maxHeight: isMobileSheet ? "1px" : "2px",
							flexShrink: 0,
						}}
					/>
				</>
			)}
			{childrenContent}
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backdropFilter: "saturate(0.2)",
						zIndex: 100001,
					}}
				/>
			)}
		</Box>
	);

	let panel = <>{content}</>;

	if (decorator === "absolute") {
		if (isMobileSheet) {
			panel = (
				<Box
					onClick={handleClose}
					sx={{
						position: "fixed",
						top: "0",
						left: "0",
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-end",
						zIndex: 1000,
						backgroundColor: sheetVisible ? "rgba(0,0,0,.5)" : "rgba(0,0,0,0)",
						transition: "background-color 0.3s ease",
					}}>
					<Box
						onClick={(e: any) => e.stopPropagation()}
						sx={{
							transform: sheetVisible ? "translateY(0)" : "translateY(100%)",
							transition: "transform 0.3s ease",
						}}>
						{content}
					</Box>
				</Box>
			);
		} else {
			panel = (
				<Box
					onClick={handleBackdropClick}
					sx={{
						position: "fixed",
						top: "0",
						left: "0",
						width: "100%",
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						zIndex: 1000,
						backgroundColor: "rgba(0,0,0,.6)",
					}}>
					{content}
				</Box>
			);
		}
	}

	return panel;
};

export default Panel;
